import { db } from "@/db";
import { poll, pollReaction } from "@/db/schema";
import { getUserSession } from "@/lib/api/auth.api";
import { auth } from "@/lib/better-auth/auth";
import { CreateReactionSchema } from "@/lib/zod-schemas/create-reaction.schema";
import { redirect } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { and, eq, sql } from "drizzle-orm";

export const createReaction = createServerFn({
  method: "POST",
})
  .inputValidator(CreateReactionSchema)
  .handler(async (ctx) => {
    const request = getRequest();
    const userSession = await auth.api.getSession({ headers: request.headers });
    if (!userSession?.user) {
      throw redirect({
        to: "/sign-in",
      });
    }

    const { pollId, type } = ctx.data;

    return await db.transaction(async (tx) => {
      const [existingReaction] = await tx
        .select()
        .from(pollReaction)
        .where(
          and(
            eq(pollReaction.userId, userSession.user.id),
            eq(pollReaction.pollId, pollId)
          )
        )
        .execute();

      if (existingReaction) {
        if (existingReaction.type === type) {
          await tx
            .delete(pollReaction)
            .where(eq(pollReaction.id, existingReaction.id))
            .execute();

          await tx
            .update(poll)
            .set({
              reactionScore:
                type === "upvote"
                  ? sql`${poll.reactionScore} - 1`
                  : sql`${poll.reactionScore} + 1`,
            })
            .where(eq(poll.id, pollId))
            .execute();

          return null;
        }

        const newReaction = await tx
          .update(pollReaction)
          .set({ type })
          .where(eq(pollReaction.id, existingReaction.id))
          .returning();

        const delta =
          existingReaction.type === "upvote" && type === "downvote"
            ? -2
            : existingReaction.type === "downvote" && type === "upvote"
              ? +2
              : 0;

        if (delta !== 0) {
          await tx
            .update(poll)
            .set({
              reactionScore:
                delta > 0
                  ? sql`${poll.reactionScore} + ${delta}`
                  : sql`${poll.reactionScore} - ${Math.abs(delta)}`,
            })
            .where(eq(poll.id, pollId))
            .execute();
        }

        return newReaction;
      } else {
        const newReaction = await tx
          .insert(pollReaction)
          .values({
            userId: userSession.user.id,
            pollId: pollId,
            type,
          })
          .returning();

        // Upvote => +1, Downvote => -1
        await tx
          .update(poll)
          .set({
            reactionScore:
              type === "upvote"
                ? sql`${poll.reactionScore} + 1`
                : sql`${poll.reactionScore} - 1`,
          })
          .where(eq(poll.id, pollId))
          .execute();

        return newReaction;
      }
    });
  });
